import styled, { css } from 'styled-components'
import { getFontStyle } from '@diversioteam/diversio-ds'

import { SideNavContainerProps, HeaderProps, ContentProps, NavItemWrapperProps, NavButtonProps } from './SideNav.types'

export const SideNavContainer = styled.div<SideNavContainerProps>`
    background-color: #5e4ae3;
    height: ${(props) => (props.$isMobile ? '100%' : '100vh')};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: ${(props) => (props.$isExpanded ? '256px' : '70px')};
    transition: width 0.25s ease;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 40;
    box-shadow: ${(props) => (props.$isExpanded ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none')};

    /* Ensure desktop sidebar doesn't cause horizontal scroll on smaller screens */
    /* Only hide when NOT in mobile mode (desktop sidebar only) */
    @media (max-width: 768px) {
        display: ${(props) => (props.$isMobile ? 'flex' : 'none')};
        top: 0;
        height: 100%;
    }
`

export const Header = styled.div<HeaderProps>`
    padding-top: 84px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`

export const NavigationContainer = styled.div`
    flex: 1;
    padding: 4px 0;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom scrollbar styling for purple sidebar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
        transition: background 0.2s ease;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
`

export const NavItemWrapper = styled.div<NavItemWrapperProps>`
    position: relative;
    margin: 6px 8px;
    &:hover .tooltip {
        opacity: 1;
    }
`

export const NavButtonContent = styled.div<ContentProps>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 36px;
    border-radius: 8px;
    padding-left: 6px;
    gap: ${(props) => (props.$isExpanded ? '8px' : '0')};
    transition: gap 0.25s ease;
`

export const IconWrapper = styled.div`
    flex-shrink: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
        height: 20px;
        width: 20px;
    }
`

export const NotificationDot = styled.div`
    position: absolute;
    top: -0.25rem;
    right: -0.25rem;
    width: 0.75rem;
    height: 0.75rem;
    background-color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
`

export const NotificationNumber = styled.span`
    ${getFontStyle('footBold')} /* 10px, 700 weight - closest match for tiny notification number */
    color: #5e4ae3;
`

export const NavContentWrapper = styled.div<ContentProps>`
    flex: 1;
    min-width: 0;
    transition: all 200ms ease;
    ${(props) =>
        props.$isExpanded
            ? css`
                  opacity: 1;
                  transform: translateX(0);
              `
            : css`
                  opacity: 0;
                  transform: translateX(-0.5rem);
              `}
`

export const NavTextContent = styled.div`
    text-align: left;
`

export const NavItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
`

export const NavLabel = styled.span`
    ${getFontStyle('p02Medium')} /* 14px, 500 weight - matches text-sm font-medium */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const NavDescription = styled.p`
    ${getFontStyle('p03Regular')} /* 12px, 400 weight - matches text-xs */
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.25;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
`

export const Tooltip = styled.div`
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 0.5rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 200ms;
    z-index: 50;
`

export const TooltipContent = styled.div`
    ${getFontStyle('p02Regular')} /* 14px, 400 weight - matches text-sm */
    background-color: #111827;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    white-space: nowrap;
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
`

export const Footer = styled.div`
    padding: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    margin-top: auto;
`

export const FooterContent = styled.div<ContentProps>`
    transition: all 200ms ease;
    opacity: ${(props) => (props.$isExpanded ? '1' : '0')};
`

export const FooterText = styled.div`
    ${getFontStyle('p03Regular')} /* 12px, 400 weight - matches text-xs */
    color: rgba(255, 255, 255, 0.7);
`

export const FooterPrompt = styled.p`
    ${getFontStyle('p03Regular')} /* 12px, 400 weight - for footer text */
    margin-bottom: 0.25rem;
`

// Navigation button styled component to replace custom Button variant="nav"
export const NavButton = styled.button<NavButtonProps>`
    ${getFontStyle('p02Medium')} /* 14px, 500 weight */
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    width: 100%;
    min-height: 44px;
    padding: 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid transparent;
    cursor: pointer;
    outline: none;
    transition: all 0.2s;
    touch-action: manipulation;
    ${(props) =>
        props.$isActive
            ? css`
                  background-color: rgba(255, 255, 255, 0.2); /* bg-white/20 */
                  color: white;

                  &:hover:not(:disabled) {
                      background-color: rgba(255, 255, 255, 0.25); /* hover:bg-white/25 */
                  }
              `
            : css`
                  color: rgba(255, 255, 255, 0.8); /* text-white/80 */

                  &:hover:not(:disabled) {
                      background-color: rgba(255, 255, 255, 0.1); /* hover:bg-white/10 */
                      color: white;
                  }
              `}

    &:disabled {
        pointer-events: none;
        opacity: 0.5;
    }

    &:focus-visible {
        border-color: var(--ring);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--ring) 50%, transparent);
    }
`

// Footer link button styled component to replace custom Button variant="link"
export const FooterLinkButton = styled.button`
    ${getFontStyle('p03Regular')} /* 12px, 400 weight */
    background-color: transparent;
    color: white;
    text-decoration: none;
    padding: 0;
    border: none;
    cursor: pointer;
    min-height: 44px;
    touch-action: manipulation;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        color: rgba(255, 255, 255, 0.8); /* hover:text-white/80 */
        text-decoration: underline;
    }

    &:disabled {
        pointer-events: none;
        opacity: 0.5;
    }

    &:focus-visible {
        outline: 2px solid white;
        outline-offset: 2px;
    }
`
